import { useContext, useEffect, useMemo, useState } from "react";
import { convertTo24HourFormat, dateConvert, generateTimeSlots, timeStringToDate } from "../../../utils/Helper";
import services from "../../../services/services";
import { Urls } from "../../../services/Urls";
import { ToastContext } from "../../../context/ToastContext"
import _ from "underscore";
import { callautopopulate } from "../../../services/callautopop";

export const useCabinBookingForm = ({ shiftTime, selectedCabin, selectedDate, closePopup, callApi, displaySlot, editData, gridData }) => {

    const { showSuccess, showError, showInfo } = useContext(ToastContext);
    const [options, setOptions] = useState({ duration: [{ label: "15 minutes", value: 15 }, { label: "30 minutes", value: 30 }, { label: "1 hour", value: 60 }], users: [] });
    const [bookingValues, setBookingValues] = useState({ duration: 30, slot: [], users: [], reason: "" });
    const [timeSlots, setTimeSlots] = useState([]);
    const [loader, setLoader] = useState({ save: false, cancel: false });
    const [showCancelBooking, setShowCancelBooking] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);

    useEffect(() => {
        callautopopulate("all_users")?.then((res) => {
            setOptions((prev) => ({ ...prev, users: _.sortBy(res?.all_users, "user_display_name") }));
        })

        let duration = 30;
        if(!_.isEmpty(displaySlot?.disable)) {
            let allDuration = displaySlot?.disable?.map((item) => Math.abs(parseInt(item?.end?.split(":")?.[1]) - parseInt(item?.start?.split(":")?.[1])));
            allDuration = allDuration.map(item => item === 0 ? 60 : item);
            duration = Math.min(...allDuration);
            duration = ![15, 30, 60]?.includes(duration) ? 15 : duration
        }
        setBookingValues((prev) => ({...prev, duration}))
    }, [])

    useEffect(() => {
        if (!_.isEmpty(editData)) {

            // show cancel button
            setShowCancelBooking((new Date() < timeStringToDate(editData?.booking_time?.start, selectedDate)) && (new Date() < timeStringToDate(editData?.booking_time?.end, selectedDate)));

            // set pre-filled fields
            let users = [];
            options?.users?.forEach((item) => {
                if (editData?.participant_user_codes?.includes(item?.user_code)) {
                    users.push(item);
                }
            })

            let duration = Math.abs(editData?.minutes) === 45 ? 15 : Math.abs(editData?.minutes);

            // change duration according to booking - only for future date
            const filteredBookingData = _.pluck(_.filter((_.pluck(gridData, selectedCabin?.field)?.flatMap(item => item)), { booking_id: editData?.booking_id }), "booking_time");
            if (displaySlot?.is_future || filteredBookingData?.length > 1) {
                let allDuration = filteredBookingData?.map((item) => Math.abs(parseInt(item?.end?.split(":")?.[1]) - parseInt(item?.start?.split(":")?.[1])));
                allDuration = allDuration.map(item => item === 0 ? 60 : item);
                duration = Math.min(...allDuration)
            }

            const bookedSlots = generateTimeSlots(duration, editData?.booking_time?.start, editData?.booking_time?.end, true);
            setBookingValues((prev) => ({ ...prev, reason: editData?.booking_title, users: users, duration: duration, slot: _.isEmpty(bookingValues?.slot) ? bookedSlots : bookingValues?.slot }))
        }
    }, [editData, options?.users])

    useEffect(() => {
        formatTimeSlots();
        _.isEmpty(editData) && setBookingValues((prev) => ({ ...prev, slot: [] }));
    }, [bookingValues?.duration])

    const formatTimeSlots = () => {
        // only when booking cabin for future date
        const filteredData = _.pluck(_.filter((_.pluck(gridData, selectedCabin?.field)?.flatMap(item => item)), { booking_id: editData?.booking_id }), "booking_time");

        let duration = bookingValues?.duration;
        if (!_.isEmpty(editData) && filteredData?.[0]?.start?.split(":")?.[1] === "15") {
            duration = 15;
        }

        const filteredBookingData = _.pluck(generateTimeSlots(duration, filteredData?.[0]?.start, filteredData?.[filteredData?.length - 1]?.end, true), "value");

        let startSlotTime = displaySlot?.time?.cell_start ? displaySlot?.time?.cell_start : shiftTime?.from;
        let endSlotTime = displaySlot?.time?.cell_end <= shiftTime?.to ? displaySlot?.time?.cell_end : shiftTime?.to;

        if (filteredData?.length > 1) {
            startSlotTime = filteredData?.[0]?.start > startSlotTime ? startSlotTime : filteredData?.[0]?.start;
            endSlotTime = filteredData?.[filteredData?.length - 1]?.end > endSlotTime ? filteredData?.[filteredData?.length - 1]?.end : endSlotTime;
        }

        const allSlots = generateTimeSlots(duration, startSlotTime, endSlotTime, true);
        let disableSlots = displaySlot?.disable;
        if (filteredData?.length > 1) {
            disableSlots = _.uniq(disableSlots?.concat(filteredData))
        }
        // disable slot getting randomly sorted
        const showEnabledSlots = [];
        allSlots?.map((slot) => {
            disableSlots?.forEach((disSlot) => {
                if ((new Date() >= timeStringToDate(slot?.value?.end, selectedDate))) {
                    slot["disable"] = true;
                }
                if (_.isEqual(slot?.value, disSlot) || ((slot?.value?.start <= disSlot?.start && slot?.value?.end >= disSlot?.end) || (slot?.value?.start >= disSlot?.start && slot?.value?.end <= disSlot?.end))) {
                    if (!_.isEmpty(editData)) {
                        if (_.isEqual(disSlot, editData?.booking_time) || (slot?.value?.start >= editData?.booking_time?.start && slot?.value?.end <= editData?.booking_time?.end) || ((displaySlot?.is_future || filteredData?.length > 1) && _.some(filteredBookingData, (item) => _.isEqual(item, slot?.value)))) {
                            showEnabledSlots?.push(slot)
                            slot["show_enabled"] = true;
                        } else {
                            slot["disable"] = true;
                        }
                    } else {
                        slot["disable"] = true;
                    }
                }
            })
        })
        !_.isEmpty(showEnabledSlots) && setBookingValues((prev) => ({ ...prev, slot: showEnabledSlots, duration: duration }))
        setTimeSlots(allSlots);
    }

    const handleSaveBooking = async () => {
        try {
            if (_.isEmpty(bookingValues?.slot)) {
                showInfo("Info", "Please select a slot to book");
                return;
            } else if (!bookingValues?.reason) {
                showInfo("Info", "Please enter the booking title");
                return;
            }
            setLoader((prev) => ({ ...prev, save: true }));
            const payload = {
                booking_id: !_.isEmpty(editData) ? editData?.booking_id : 0,
                cabin_id: selectedCabin?.cabin_id,
                from_datetime: dateConvert(timeStringToDate(bookingValues?.slot?.[0]?.value?.start, selectedDate), "iso-string") || "",
                to_datetime: dateConvert(timeStringToDate(bookingValues?.slot?.[bookingValues?.slot?.length - 1].value?.end, selectedDate), "iso-string") || "",
                booking_title: bookingValues?.reason,
                participant_user_codes: _.pluck(bookingValues?.users, "user_code")?.toString()
            }
            const res = await services.getInvokeRemote("POST", Urls("UPSERTMYMEETINGCABIN"), payload);
            if (res) {
                showSuccess("Success", res?.body?.message);
                callApi();
                closePopup();
                setLoader((prev) => ({ ...prev, save: false }));
            }
        } catch (error) {
            console.error("Error: ", error);
            setLoader((prev) => ({ ...prev, save: false }));
        }
    }

    const handleDeleteBooking = async () => {
        try {
            setLoader((prev) => ({ ...prev, cancel: true }));
            const payload = {
                booking_id: editData?.booking_id
            }
            const res = await services.getInvokeRemote("POST", Urls("DELETEBOOKCABIN"), payload);
            if (res) {
                showSuccess("Success", res?.body?.message);
                callApi();
                closePopup();
                setLoader((prev) => ({ ...prev, cancel: false }));
            }
        } catch (error) {
            console.error("Error: ", error);
            showError("Error", error?.response?.data?.errorMessage);
            setLoader((prev) => ({ ...prev, cancel: false }));
            setShowConfirmation(false);
        }
    }

    const handleTimeSlotSelection = (time) => {
        let bookedSlots = [];
        if (_.some(bookingValues?.slot, (slot) => _.isEqual(slot?.value, time?.value))) {
            if ((new Date() > timeStringToDate(time?.value?.start, selectedDate) && new Date() > timeStringToDate(time?.value?.end, selectedDate))) {
                return;
            } else {
                const slots = _.filter(bookingValues?.slot, (slot) => !_.isEqual(slot?.value, time?.value) && slot?.value?.start < time?.value?.start)?.sort((a, b) => a?.value?.start?.localeCompare(b?.value?.start));
                bookedSlots = generateTimeSlots(bookingValues?.duration, slots?.[0]?.value?.start, slots?.[slots?.length - 1]?.value?.end, true);
            }
        } else {
            const slots = [...bookingValues?.slot, time]?.sort((a, b) => a?.value?.start?.localeCompare(b?.value?.start));
            bookedSlots = generateTimeSlots(bookingValues?.duration, slots?.[0]?.value?.start, slots?.[slots?.length - 1]?.value?.end, true);
            let i = 0;
            let filteredBooking = [];
            while (i < bookedSlots?.length) {
                const findData = _.findWhere(timeSlots, { label: bookedSlots?.[i]?.label });
                if (findData?.show_enabled) {
                    filteredBooking.push(bookedSlots?.[i]);
                    i++;
                } else if (findData?.disable) {
                    i = bookedSlots?.length;
                } else {
                    filteredBooking.push(bookedSlots?.[i]);
                    i++;
                }
            }
            bookedSlots = filteredBooking;
        }
        setBookingValues((prev) => ({ ...prev, slot: bookedSlots }))
    }

    return useMemo(() => ({
        options, bookingValues, setBookingValues, timeSlots, handleSaveBooking, loader, handleTimeSlotSelection, showCancelBooking, handleDeleteBooking, showConfirmation, setShowConfirmation
    }), [options, bookingValues, setBookingValues, timeSlots, handleSaveBooking, loader, handleTimeSlotSelection, showCancelBooking, handleDeleteBooking, showConfirmation, setShowConfirmation])
}